import { bridgeEventEmitter } from '@databases/rovo-bridge';
import React, { type FunctionComponent, useEffect, useState } from 'react';

import { OpenRovoSidePanelCommand } from '@canvas/databases-bridge-api/commands';
import { CONFLUENCE_AFM_CLIENT_ID } from '@canvas/databases-bridge-api/types';
import { client } from '@canvas/rovo-content-bridge-api';

import { DatabaseBlankObjectExperience } from './DatabaseBlankObjectExperience';
import { DatabaseBoeThinkingBar } from './DatabaseBoeThinkingBar';
import { useShouldShowDatabaseBlankObjectExperience } from './useShouldShowDatabaseBlankObjectExperience';

interface DatabaseBlankObjectExperienceFooterProps {
  contentId: string;
  isDbAiFirstCreationEnabled: boolean;
  isDatabaseLoaded: boolean;
  isEmptyDatabase: boolean;
  isInAIFCStagingAreaView: boolean;
}

export const DatabaseBlankObjectExperienceFooter: FunctionComponent<
  DatabaseBlankObjectExperienceFooterProps
> = ({
  contentId,
  isDbAiFirstCreationEnabled,
  isDatabaseLoaded,
  isEmptyDatabase,
  isInAIFCStagingAreaView
}) => {
  const [isRovoThinking, setIsRovoThinking] = useState<boolean>(false);

  const shouldShowBlankObjectExperience = useShouldShowDatabaseBlankObjectExperience({
    contentId,
    isEmptyDatabase,
    isDbAiFirstCreationEnabled,
    isDatabaseLoaded,
    isInAIFCStagingAreaView
  });

  const handleSubmit = () => {
    setIsRovoThinking(true);
  };

  const handleThinkingDismiss = () => {
    setIsRovoThinking(false);
  };

  const handleThinkingComplete = () => {
    bridgeEventEmitter.sendEvent(OpenRovoSidePanelCommand, {
      senderId: client(`confluence-database-${contentId}`),
      receiverId: CONFLUENCE_AFM_CLIENT_ID
    });
    setIsRovoThinking(false);
  };

  // Reset local thinking state if this footer is reused for a different database.
  useEffect(() => {
    setIsRovoThinking(false);
  }, [contentId]);

  if (isRovoThinking) {
    return (
      <DatabaseBoeThinkingBar
        contentId={contentId}
        onDismiss={handleThinkingDismiss}
        onStreamComplete={handleThinkingComplete}
        placement="inline"
      />
    );
  }

  if (shouldShowBlankObjectExperience) {
    return (
      <DatabaseBlankObjectExperience contentId={contentId} onSubmit={handleSubmit} />
    );
  }

  return null;
};
